/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import { capitalize } from '@/services/utils';
import axios, { AxiosError, AxiosResponse } from 'axios';
import {
  AccountAccess,
  AccountDetail,
  AccountSettings,
  CompanyDetail,
  NextReferences,
  Permissions,
} from './accountDetail';
import { UseMeStoreState } from './useMeStoreState';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';

export const useMeStore = defineStore('meStore', {
  state: (): UseMeStoreState => {
    const { handleError } = useSnackbarHandler();
    return {
      handleError,
      me: {} as AccountDetail,
      terms: {} as Record<string, string>,
      access: {} as AccountAccess,
      features: {} as Record<string, boolean>,
      settings: {} as AccountSettings,
      company: {} as CompanyDetail,
      permissions: {} as Permissions,
      nextClientReference: null,
    };
  },
  actions: {
    async getMe(forcereset = false): Promise<AccountDetail> {
      try {
        if (Object.keys(this.me).length && !forcereset) {
          return this.me;
        }
        const response: AxiosResponse<AccountDetail> = await axios({
          url: '/me',
        });
        Object.entries(response.data.terms).forEach(([term, str]) => {
          response.data.terms[capitalize(term)] = capitalize(str);
        });
        // Initialize label property like Angular UserService does
        response.data.label = response.data.first;
        this.$patch({
          me: response.data,
          terms: response.data.terms,
          features: response.data.features,
          settings: response.data.settings,
          company: response.data.company,
          permissions: response.data.permissions,
          access: response.data.access,
        });
        return response.data;
      } catch (error: AxiosError | unknown) {
        this.handleError(error);
        return {} as AccountDetail;
      }
    },

    // Partial, not AccountDetail - every real caller (Time.vue, InvoiceButtons.vue,
    // this store's own settings-patch callers) sends a partial update (e.g. just
    // { settings: { enableNewTimeEntry: true } }), matching the PUT /me endpoint's
    // actual partial-update semantics; this only affects the request payload's
    // shape (forwarded to axios as-is), never this method's own logic below.
    async updateMe(update: Partial<AccountDetail>): Promise<void> {
      try {
        const response: AxiosResponse<AccountDetail> = await axios({
          url: `/me`,
          method: 'PUT',
          data: update,
        });
        this.$patch({
          me: response.data,
          terms: response.data.terms,
          features: response.data.features,
          settings: response.data.settings,
          company: response.data.company,
          permissions: response.data.permissions,
          access: response.data.access,
        });
      } catch (error: AxiosError | unknown) {
        this.handleError(error);
      }
    },

    async getMyCompanyReferences(
      clientId: string | null = null,
    ): Promise<NextReferences> {
      try {
        const params: { clientId?: string } = {};
        if (clientId) {
          params.clientId = clientId;
        }

        const response: AxiosResponse<NextReferences> = await axios({
          url: `/me/company/references`,
          method: 'GET',
          params,
        });
        return response.data;
      } catch (error: AxiosError | unknown) {
        this.handleError(error);
        return [] as any;
      }
    },

    async getFixedFeeColumns(): Promise<any> {
      try {
        const response = await axios({
          url: `/me/fixed-fees/columns`,
          method: 'GET',
        });
        return response.data;
      } catch (error: AxiosError | unknown) {
        this.handleError(error);
        return null;
      }
    },

    async updateFixedFeeColumns(visibleColumns: string[]): Promise<any> {
      try {
        const response = await axios({
          url: `/me/fixed-fees/columns`,
          method: 'PUT',
          data: { visibleColumns },
        });
        
        // Update local settings state using $patch for proper reactivity
        this.$patch({
          settings: {
            ...this.settings,
            fixedFeeTableColumns: {
              ...this.settings?.fixedFeeTableColumns,
              visibleColumns,
            },
          },
        });
        
        return response.data;
      } catch (error: AxiosError | unknown) {
        this.handleError(error);
        throw error;
      }
    },

    async updateFixedFeeInsightsVisibility(visible: boolean): Promise<void> {
      // Update local settings state first (optimistic update)
      this.$patch((state) => {
        if (!state.settings) {
          state.settings = {} as AccountSettings;
        }
        state.settings.fixedFeeInsightsVisible = visible;
      });

      try {
        const payload = { settings: { fixedFeeInsightsVisible: visible } };
        await axios.put(`accounts/${this.me.id}`, payload);
      } catch (error: AxiosError | unknown) {
        console.error('Failed to persist Fixed Fee insights visibility:', error);
        // Keep the optimistic update - state will sync on next successful request
      }
    },
  },
});
